import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { BACKEND_BASE_URL } from "../constants";
import { TextBackground } from "../components";
import {
    List,
	ListItem,
    ListItemButton,
    ListItemText,
    Typography,
    Paper,
    Grid,
    Box,
    CircularProgress,
    Alert,
	Button,
} from "@mui/material";

// Import Molstar dependencies
import { createPluginUI } from "molstar/lib/mol-plugin-ui";
import { renderReact18 } from "molstar/lib/mol-plugin-ui/react18";
import { PluginUIContext } from "molstar/lib/mol-plugin-ui/context";
import { DefaultPluginUISpec } from 'molstar/lib/mol-plugin-ui/spec';
import "molstar/lib/mol-plugin-ui/skin/light.scss";

// --- Definitions of data structures returned by /task_status/ API ---

// Structure for a single PDB file
interface StructureFile {
    priority: number;
    pdb_file_path: string;
}

// Structure for all job results
interface JobResultData {
    pmhc: StructureFile[];
    tcr: StructureFile[];
    complex: StructureFile[];
}

// Structure for job parameters
interface JobParams {
    TCRalpha: string;
    TCRbeta: string;
    peptide: string;
    mhc: string;
}

// /task_status/ API Response structure
interface JobStatusData {
    status: "PENDING" | "STARTED" | "SUCCESS" | "FAILURE";
    params: JobParams;
    results?: JobResultData;
    error?: string;
    tar_result_url?: string;
}
// --------------------------------------------------------

export const Results = () => {
    const { job_id } = useParams<{ job_id: string }>();
    const [jobData, setJobData] = useState<JobStatusData | null>(null);
    const [selectedPdbUrl, setSelectedPdbUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const molstarContainerRef = useRef<HTMLDivElement>(null);
    const molstarPlugin = useRef<PluginUIContext | null>(null);
    const pollInterval = useRef<NodeJS.Timeout | null>(null);

    // Continuously poll job status until completion or failure
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const response = await fetch(`${BACKEND_BASE_URL}/stegg/job/${job_id}/task_status/`);
                if (!response.ok) {
                    throw new Error("Failed to fetch job status.");
                }
                const data: JobStatusData = await response.json();
                setJobData(data);
                setIsLoading(false);

                if (data.status === "SUCCESS") {
                    setError(null);
                    if (pollInterval.current) clearInterval(pollInterval.current);

                    if (data.results && data.results.complex && data.results.complex.length > 0) {
                        const sortedComplex = [...data.results.complex].sort((a, b) => a.priority - b.priority);
                        setSelectedPdbUrl(sortedComplex[0].pdb_file_path);
                    }
                } else if (data.status === "FAILURE") {
                    setError(data.error || "Job failed for an unknown reason.");
                    if (pollInterval.current) clearInterval(pollInterval.current);
                }
            } catch (err) {
                const errorMsg = (err instanceof Error) ? err.message : "An unknown error occurred.";
                setError(errorMsg);
                setIsLoading(false);
                if (pollInterval.current) clearInterval(pollInterval.current);
            }
        };

        fetchStatus();
        pollInterval.current = setInterval(fetchStatus, 5000);

        return () => {
            if (pollInterval.current) {
                clearInterval(pollInterval.current);
            }
        };
    }, [job_id]);

    // Initialize and update Molstar view
    useEffect(() => {
        const initMolstar = async () => {
            if (!molstarContainerRef.current) return;
            
            if (!molstarPlugin.current) {
                const spec = DefaultPluginUISpec();
                spec.layout = {
                    initial: { // Use 'initial' to set the starting layout state
                        showControls: false,
                        isExpanded: false,
                        regionState: {
                            top: 'hidden',
                            bottom: 'hidden',
                            left: 'hidden',
                            right: 'hidden',
                        },
                    },
                };                   
                molstarPlugin.current = await createPluginUI({
                    target: molstarContainerRef.current,
                    render: renderReact18,
                    spec,
                });
       
            }

            if (selectedPdbUrl && molstarPlugin.current) {
                await molstarPlugin.current.clear();
                const absoluteUrl = `${BACKEND_BASE_URL}${selectedPdbUrl}`;
                try {
                    const data = await molstarPlugin.current.builders.data.download({ url: absoluteUrl });
                    const trajectory = await molstarPlugin.current.builders.structure.parseTrajectory(data, "pdb");
                    await molstarPlugin.current.builders.structure.hierarchy.applyPreset(trajectory, "default");
                } catch (e) {
                    console.error("Mol* failed to load PDB:", e);
                    setError(`Failed to load 3D model from ${absoluteUrl}`);
                }
            }
        };
        initMolstar();
    }, [selectedPdbUrl]);

    // --- Render Component ---

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <section className="page">
                <TextBackground>
                    <Typography variant="h4" color="error" gutterBottom>Job Failed</Typography>
                    <Alert severity="error">{error}</Alert>
                </TextBackground>
            </section>
        );
    }

    if (!jobData) {
        return null;
    }

    if (jobData.status === "PENDING" || jobData.status === "STARTED") {
        return (
            <section className="page">
                <TextBackground>
                    <Typography variant="h4" gutterBottom>Job is Running</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography>Your job is being processed... The status will update automatically.</Typography>
                    </Box>
                </TextBackground>
            </section>
        );
    }

    if (jobData.status === "SUCCESS") {
        
        // --- Generate download URL if available ---
        const downloadUrl = jobData.tar_result_url
            ? `${BACKEND_BASE_URL}${jobData.tar_result_url}`
            : null;

        return (
            <section className="page">
                <TextBackground>
                    {/* Title and download button */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h4" gutterBottom>Job Results: {job_id}</Typography>

                        {/* --- New: Download button --- */}
                        {downloadUrl && (
                            <Button
                                variant="contained"
                                color="primary"
                                href={downloadUrl}
                                download // Tell the browser this is a download link
                                sx={{ textTransform: 'none' }}
                            >
                                Download All Results (.tar.gz)
                            </Button>
                        )}
                    </Box>

                    {/* Job Parameters */}
                    <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: '#f9f9f9' }}>
                        <Typography sx={{ wordBreak: 'break-all' }}>
                            <strong>Peptide:</strong> {jobData.params.peptide}
                        </Typography>
                        <Typography sx={{ wordBreak: 'break-all' }}>
                            <strong>TCR Alpha:</strong> {jobData.params.TCRalpha}
                        </Typography>
                        <Typography sx={{ wordBreak: 'break-all' }}>
                            <strong>TCR Beta:</strong> {jobData.params.TCRbeta}
                        </Typography>
                        <Typography sx={{ wordBreak: 'break-all' }}>
                            <strong>MHC:</strong> {jobData.params.mhc}
                        </Typography>
                    </Paper>

                    <Grid container spacing={3}>
                        {/* Left: Complex Structures List */}
                        <Grid item xs={12} md={3}>
                            <Typography variant="h6">Complex Structures</Typography>
                            <Paper elevation={1} sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                                <List>
                                    {jobData.results && jobData.results.complex && jobData.results.complex.length > 0 ? (
                                        [...jobData.results.complex].sort((a, b) => a.priority - b.priority).map((structure) => (
                                            <ListItemButton 
                                                key={structure.priority}
                                                onClick={() => setSelectedPdbUrl(structure.pdb_file_path)}
                                                selected={selectedPdbUrl === structure.pdb_file_path}
                                            >
                                                <ListItemText primary={`_complex_${structure.priority}_roi.pdb`} />
                                            </ListItemButton>
                                        ))
                                    ) : (
                                        <ListItem>
                                            <ListItemText primary="No complex files found." />
                                        </ListItem>
                                    )}
                                </List>
                            </Paper>
                        </Grid>
                        
                        {/* Right: 3D View */}
                        <Grid item xs={12} md={9}>
                            <Box 
                                ref={molstarContainerRef}
                                sx={{ 
                                    position: 'relative', 
                                    width: '100%', 
                                    height: 0, 
                                    paddingTop: '100%',
                                    border: '1px solid #ccc' 
                                }} 
                            />
                        </Grid>
                    </Grid>
                </TextBackground>
            </section>
        );
    }

    return (
        <section className="page">
            <TextBackground>
                <Typography>Unknown job status: {jobData.status}</Typography>
            </TextBackground>
        </section>
    );
};